// User role constants
export const USER_ROLES = {
  BENEFICIARY: 1,
  PROJECT_DEVELOPER: 2,
  BOTH: 3,
  BROKER: 4
} as const

export const USER_ROLE_NAMES = {
  1: "BENEFICIARY",
  2: "PROJECT_DEVELOPER",
  3: "BOTH",
  4: "BROKER"
} as const

export type UserRole = keyof typeof USER_ROLES

// Auth user type constants
export const AUTH_USER_TYPES = {
  INDIVIDUAL: "individual",
  COMPANY: "company",
  ORGANIZATION: "organization"
} as const

export type AuthUserType = typeof AUTH_USER_TYPES[keyof typeof AUTH_USER_TYPES]

// Auth user status constants
export const AUTH_USER_STATUS = {
  EMAIL_PENDING: "email_pending",
  PROFILE_INCOMPLETE: "profile_incomplete",
  PENDING: "pending",
  PENDING_STRIPE_KYC: "pending_stripe_kyc",
  ACTIVE: "active",
  SUSPENDED: "suspended"
} as const

export type AuthUserStatus = typeof AUTH_USER_STATUS[keyof typeof AUTH_USER_STATUS]

// Token status constants
export const TOKEN_STATUS = {
  ACTIVE: 1,
  RETIRED: 2,
  REVERSED: 3,
  INSURANCE_POOL: 4,      // available pool token — non-tradeable, non-retirable
  LOCKED: 5,
  INSURANCE_RESERVED: 6  // earmarked for a specific replacement event, atomically reserved
} as const

export const TOKEN_STATUS_NAMES = {
  1: "ACTIVE",
  2: "RETIRED",
  3: "REVERSED",
  4: "INSURANCE_POOL",
  5: "LOCKED",
  6: "INSURANCE_RESERVED"
} as const

export type TokenStatus = keyof typeof TOKEN_STATUS

// External merchant retail-retirement ledger (Manuelitas integration)
export const MERCHANT_STATUS = { ACTIVE: 1, SUSPENDED: 2 } as const
export type MerchantStatus = keyof typeof MERCHANT_STATUS

export const RETIREMENT_TONNE_STATUS = { OPEN: 1, CLOSED: 2 } as const
export type RetirementTonneStatus = keyof typeof RETIREMENT_TONNE_STATUS

// Where a merchant's tonne came from. OWNED is the only kind created now: a credit the
// merchant's own organisation holds — bought on the exchange like any buyer (by hand from its
// dashboard, or by the automatic top-up) or already held — priced at what the merchant paid for
// it. Carbonomex is the market and owns no stock (30 Sept 2026). PREBOOK (an admin locking a
// third party's token at its last price) and AUTO_TOP_UP remain only for tonnes created before.
export const RETIREMENT_TONNE_SOURCE = { PREBOOK: 1, AUTO_TOP_UP: 2, OWNED: 3 } as const

// QUEUED: accepted and serialised, but no prebooked tonne had room yet — backed oldest-first as
// soon as one does (see apps/marketplace/src/lib/merchant-tonnes.ts). Never shown as retired.
export const ALLOCATION_STATUS = { PRE_BOOKED: 1, RETIRED: 2, FAILED: 3, QUEUED: 4 } as const
export type AllocationStatus = keyof typeof ALLOCATION_STATUS

// The wire-string form of ALLOCATION_STATUS for POST/GET /api/external/allocations — defined
// once here so apps/marketplace (produces it) and apps/registry (renders it on /record) can't
// drift into disagreeing on what a status code means.
export const ALLOCATION_STATUS_WIRE = {
  [ALLOCATION_STATUS.PRE_BOOKED]: "pre_booked",
  [ALLOCATION_STATUS.RETIRED]: "retired",
  [ALLOCATION_STATUS.FAILED]: "failed",
  [ALLOCATION_STATUS.QUEUED]: "queued",
} as const

// Offset type constants
export const OFFSET_TYPES = {
  OFFSET_50: 50,
  OFFSET_100: 100
} as const

export const OFFSET_TYPE_NAMES = {
  50: "OFFSET_50",
  100: "OFFSET_100"
} as const

export type OffsetType = keyof typeof OFFSET_TYPES

// Project type constants (deprecated — use CREDIT_CLASS on sink_vintages instead)
export const PROJECT_TYPES = {
  EX_ANTE: 1,
  EX_POST: 2
} as const

export const PROJECT_TYPE_NAMES = {
  1: "EX_ANTE",
  2: "EX_POST"
} as const

export type ProjectType = keyof typeof PROJECT_TYPES

// Credit class — per vintage: ex-ante (future/projected) vs ex-post (verified/occurred)
export const CREDIT_CLASS = { EX_ANTE: 1, EX_POST: 2 } as const
export const CREDIT_CLASS_NAMES = { 1: "EX_ANTE", 2: "EX_POST" } as const
export const CREDIT_CLASS_LABELS = { 1: "Ex-Ante", 2: "Ex-Post" } as const
export type CreditClass = typeof CREDIT_CLASS[keyof typeof CREDIT_CLASS]

// Organization type constants
export const ORGANIZATION_TYPES = {
  INDIVIDUAL: 10,
  COMPANY: 20,
  ORGANIZATION: 30
} as const

export const ORGANIZATION_TYPE_NAMES = {
  10: "INDIVIDUAL",
  20: "COMPANY",
  30: "ORGANIZATION"
} as const

export type OrganizationType = keyof typeof ORGANIZATION_TYPES

// Certification status constants
export const CERTIFICATION_STATUS = {
  ACTIVE: 1,
  EXPIRED: 2,
  SUSPENDED: 3,
  REVOKED: 4
} as const

export const CERTIFICATION_STATUS_NAMES = {
  1: "ACTIVE",
  2: "EXPIRED",
  3: "SUSPENDED",
  4: "REVOKED"
} as const

export type CertificationStatus = keyof typeof CERTIFICATION_STATUS

// UI mode constants
export const UI_MODE = {
  STANDARD: 'standard',
  TRADER: 'trader',
} as const

export type UiMode = typeof UI_MODE[keyof typeof UI_MODE]

// Transaction type constants
export const TRANSACTION_TYPES = {
  ISSUANCE: 1,
  TRADE: 2,
  RETIREMENT: 4
} as const

export const TRANSACTION_TYPE_NAMES = {
  1: "ISSUANCE",
  2: "TRADE",
  4: "RETIREMENT"
} as const

export type TransactionType = keyof typeof TRANSACTION_TYPES

// SDG (Sustainable Development Goals) constants
export const SDG_GOALS = {
  NO_POVERTY: 1,
  ZERO_HUNGER: 2,
  GOOD_HEALTH: 3,
  QUALITY_EDUCATION: 4,
  GENDER_EQUALITY: 5,
  CLEAN_WATER: 6,
  AFFORDABLE_ENERGY: 7,
  DECENT_WORK: 8,
  INDUSTRY_INNOVATION: 9,
  REDUCED_INEQUALITIES: 10,
  SUSTAINABLE_CITIES: 11,
  RESPONSIBLE_CONSUMPTION: 12,
  CLIMATE_ACTION: 13,
  LIFE_BELOW_WATER: 14,
  LIFE_ON_LAND: 15,
  PEACE_JUSTICE: 16,
  PARTNERSHIPS: 17
} as const

export const SDG_GOAL_NAMES = {
  1: "NO_POVERTY",
  2: "ZERO_HUNGER", 
  3: "GOOD_HEALTH",
  4: "QUALITY_EDUCATION",
  5: "GENDER_EQUALITY",
  6: "CLEAN_WATER",
  7: "AFFORDABLE_ENERGY",
  8: "DECENT_WORK",
  9: "INDUSTRY_INNOVATION",
  10: "REDUCED_INEQUALITIES",
  11: "SUSTAINABLE_CITIES",
  12: "RESPONSIBLE_CONSUMPTION",
  13: "CLIMATE_ACTION",
  14: "LIFE_BELOW_WATER",
  15: "LIFE_ON_LAND",
  16: "PEACE_JUSTICE",
  17: "PARTNERSHIPS"
} as const

export type SDGGoal = keyof typeof SDG_GOALS

// Trading enums
export const TRADE_ORDER_SIDE = { BID: 1, ASK: 2 } as const
export const TRADE_ORDER_SIDE_NAMES = { 1: "BID", 2: "ASK" } as const
export type TradeOrderSide = typeof TRADE_ORDER_SIDE[keyof typeof TRADE_ORDER_SIDE]

export const TRADE_ORDER_TYPE = { LIMIT: 1, MARKET: 2, STOP: 3 } as const
export const TRADE_ORDER_TYPE_NAMES = { 1: "LIMIT", 2: "MARKET", 3: "STOP" } as const
export type TradeOrderType = typeof TRADE_ORDER_TYPE[keyof typeof TRADE_ORDER_TYPE]

export const TRADE_ORDER_STATUS = { OPEN: 1, PARTIAL: 2, FILLED: 3, CANCELLED: 4 } as const
export const TRADE_ORDER_STATUS_NAMES = { 1: "OPEN", 2: "PARTIAL", 3: "FILLED", 4: "CANCELLED" } as const
export type TradeOrderStatus = typeof TRADE_ORDER_STATUS[keyof typeof TRADE_ORDER_STATUS]

export const TRADE_FILL_TYPE = { PARTIAL: 1, ALL_OR_NOTHING: 2 } as const
export const TRADE_FILL_TYPE_NAMES = { 1: "PARTIAL", 2: "ALL_OR_NOTHING" } as const
export type TradeFillType = typeof TRADE_FILL_TYPE[keyof typeof TRADE_FILL_TYPE]

// Settlement status constants
// NOTE: Values start at 5 intentionally to avoid colliding with TRADE_ORDER_STATUS (1–4).
// Do NOT add values 5–10 to TRADE_ORDER_STATUS. These two enums share a numeric space
// but target different columns (tradeOrders.status vs tradeOrders.settlementStatus).
export const SETTLEMENT_STATUS = {
  AGREED: 5,
  SEGREGATED_ACCOUNT_PENDING: 6,
  FUNDS_RECEIVED: 7,
  CREDITS_TRANSFERRED: 8,
  SETTLEMENT_COMPLETE: 9,
  FAILED: 10,
  // Bank-wire manual settlement (regular OTC, or broker-option exercise) accepted the wire and is
  // transferring credits — distinct from AGREED (a normal order still going through Stripe
  // capture) so every consumer of settlementStatus can tell the two apart by value alone, instead
  // of also having to check trade_orders.isManualSettlement. Never reached via the normal
  // AGREED-driven Stripe pipeline; only markManualSettlement/confirmExerciseFunds write it, and
  // only finalizeManualSettlement (or its stale-order recovery backstop) flips it to COMPLETE.
  MANUAL_SETTLEMENT_PENDING: 11,
} as const
export const SETTLEMENT_STATUS_NAMES = {
  5: "AGREED", 6: "SEGREGATED_ACCOUNT_PENDING", 7: "FUNDS_RECEIVED",
  8: "CREDITS_TRANSFERRED", 9: "SETTLEMENT_COMPLETE", 10: "FAILED",
  11: "MANUAL_SETTLEMENT_PENDING",
} as const
export type SettlementStatus = typeof SETTLEMENT_STATUS[keyof typeof SETTLEMENT_STATUS]

export const SEGREGATED_ACCOUNT_STATUS = {
  PENDING: "pending", FUNDED: "funded", RELEASED: "released",
  COMPLETED: "completed", FAILED: "failed",
} as const
export type SegregatedAccountStatus = typeof SEGREGATED_ACCOUNT_STATUS[keyof typeof SEGREGATED_ACCOUNT_STATUS]

// Procurement mandate status constants
export const PROCUREMENT_MANDATE_STATUS = {
  DRAFT: 1, ACTIVE: 2, MATCHED: 3, EXECUTED: 4, EXPIRED: 5, ARCHIVED: 6
} as const
export const PROCUREMENT_MANDATE_STATUS_NAMES = {
  1: "DRAFT", 2: "ACTIVE", 3: "MATCHED", 4: "EXECUTED", 5: "EXPIRED", 6: "ARCHIVED"
} as const
export type ProcurementMandateStatus = typeof PROCUREMENT_MANDATE_STATUS[keyof typeof PROCUREMENT_MANDATE_STATUS]

// Broker option listing status — PD-side offer on the option board
export const OPTION_LISTING_STATUS = {
  LISTED: 1, FULLY_ACCEPTED: 2, CANCELLED: 3
} as const
export const OPTION_LISTING_STATUS_NAMES = {
  1: "LISTED", 2: "FULLY_ACCEPTED", 3: "CANCELLED"
} as const
export type OptionListingStatus = typeof OPTION_LISTING_STATUS[keyof typeof OPTION_LISTING_STATUS]

// Broker option contract status — created when a broker accepts listed blocks
export const OPTION_CONTRACT_STATUS = {
  PENDING_PREMIUM: 1, ACTIVE: 2, EXERCISE_REQUESTED: 3, EXERCISED: 4, EXPIRED: 5, CANCELLED: 6
} as const
export const OPTION_CONTRACT_STATUS_NAMES = {
  1: "PENDING_PREMIUM", 2: "ACTIVE", 3: "EXERCISE_REQUESTED", 4: "EXERCISED", 5: "EXPIRED", 6: "CANCELLED"
} as const
export type OptionContractStatus = typeof OPTION_CONTRACT_STATUS[keyof typeof OPTION_CONTRACT_STATUS]

// Why an option contract reached CANCELLED (or bounced back to ACTIVE)
export const OPTION_CANCELLATION_REASON = {
  PREMIUM_TIMEOUT: "PREMIUM_TIMEOUT",
  EXERCISE_TIMEOUT: "EXERCISE_TIMEOUT",
  ADMIN: "ADMIN",
} as const
export type OptionCancellationReason = typeof OPTION_CANCELLATION_REASON[keyof typeof OPTION_CANCELLATION_REASON]

// Order time-in-force: DAY = expires at next 09:00 Oslo session open; GTC = resting indefinitely
export const TRADE_ORDER_TIME_IN_FORCE = { DAY: "DAY", GTC: "GTC" } as const
export type TradeOrderTimeInForce = typeof TRADE_ORDER_TIME_IN_FORCE[keyof typeof TRADE_ORDER_TIME_IN_FORCE]

// Reasons an order reached status=CANCELLED (4)
export const TRADE_CANCELLATION_REASON = {
  USER_CANCELLED: "USER_CANCELLED",
  EXPIRED: "EXPIRED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  ADMIN_CANCELLED: "ADMIN_CANCELLED",
  SYSTEM_CANCELLED: "SYSTEM_CANCELLED",
} as const
export type TradeCancellationReason = typeof TRADE_CANCELLATION_REASON[keyof typeof TRADE_CANCELLATION_REASON]

// Document types for sink_documents.type field
export const SINK_DOCUMENT_TYPE = {
  VERIFICATION_DOC: "verification_doc",
  METHODOLOGY_DOC: "methodology_doc",
  MONITORING_REPORT: "monitoring_report",
  AUDIT_REPORT: "audit_report",
  HERO_IMAGE: "hero_image",
  GALLERY_IMAGE: "gallery_image",
  SATELLITE_IMAGERY: "satellite_imagery",
  EXTERNAL_REGISTRY_CERT: "external_registry_cert",
  OTHER: "other",
} as const
export type SinkDocumentType = typeof SINK_DOCUMENT_TYPE[keyof typeof SINK_DOCUMENT_TYPE]

export const SINK_DOCUMENT_TYPE_LABELS: Record<SinkDocumentType, string> = {
  verification_doc: "Verification Document",
  methodology_doc: "Methodology Document",
  monitoring_report: "Monitoring Report",
  audit_report: "Audit Report",
  hero_image: "Hero Image",
  gallery_image: "Gallery Image",
  satellite_imagery: "Satellite Imagery",
  external_registry_cert: "External Registry Certificate",
  other: "Other",
}

export const SINK_IMAGE_TYPES: SinkDocumentType[] = ["hero_image", "gallery_image"]
export const SINK_DOC_TYPES: SinkDocumentType[] = ["verification_doc", "methodology_doc", "monitoring_report", "audit_report", "external_registry_cert", "other"]

// Accreditation standard labels
export const ACCREDITATION_STANDARD_LABELS: Record<string, string> = {
  verra: "Verra (VCS)",
  gold_standard: "Gold Standard",
  puro_earth: "Puro.earth",
  cdm: "CDM",
  isometric: "Isometric",
  csi: "CSI",
  acr: "American Carbon Registry (ACR)",
  car: "Climate Action Reserve (CAR)",
  plan_vivo: "Plan Vivo",
  social_carbon: "Social Carbon",
  other: "Other",
}
